import React from 'react';
import { CheckmarkCircle } from 'src/shared/design-system/Icon/CheckmarkCircle';
import { FormErrorMessage } from 'src/shared/design-system/Input/Error/FormErrorMessage';
import { LabeledTextInput } from 'src/shared/design-system/Input/LabeledTextInput/LabeledTextInput';
import styled from 'styled-components';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';
import { AuthActionButton } from './AuthActionButton';
import { AuthSecondaryButton } from './AuthSecondaryButton';
import { LoginWithEmailProps, useLoginWithEmailForm } from './useLoginWithEmailForm';

const Banner = styled.div`
  background: ${Colors.KOHLRABI_LITE_30};
  color: ${Colors.GRAY_DARK_30};
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.S};
  font-weight: ${Font.Weight.SEMI_BOLD};

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  position: relative;
  padding: ${Spacing.HALF} ${Spacing.SINGLE_HALF} ${Spacing.HALF} ${Spacing.SINGLE_HALF};
`;

const StyledCheckmark = styled(CheckmarkCircle)`
  color: ${Colors.KOHLRABI};
  width: ${Spacing.SINGLE_HALF};
  height: ${Spacing.SINGLE_HALF};
  margin-right: ${Spacing.SINGLE};
`;

// Negative margin, since we're using gap for spacing
const ErrorMessageWithMargin = styled(FormErrorMessage)`
  margin-top: -${Spacing.HALF};
  align-self: flex-start;
`;

export const LoginWithEmail: React.FC<LoginWithEmailProps> = (props) => {
  const { email, onEmailChange, goBack, loginCTA } = props;
  const {
    password,
    onPasswordChange,
    emailErrorMessage,
    passwordErrorMessage,
    genericErrorMessage,
    bannerMessage,
    requestLoading,
    handleSubmitForm,
    handleForgotPassword,
    handleKeyDown,
  } = useLoginWithEmailForm(props);

  return (
    <>
      {bannerMessage ? (
        <Banner>
          <StyledCheckmark />
          <span>{bannerMessage}</span>
        </Banner>
      ) : null}
      <form onSubmit={handleSubmitForm} onKeyDown={handleKeyDown} data-cy='login-form'>
        <fieldset className='flex flex-col items-center gap-2 py-6' disabled={requestLoading}>
          <LabeledTextInput
            label='Email'
            name='email'
            value={email}
            onChange={onEmailChange}
            errorMessage={emailErrorMessage}
            data-cy='login-email'
            disabled
          />
          <LabeledTextInput
            password
            label='Password'
            name='password'
            value={password}
            onChange={onPasswordChange}
            errorMessage={passwordErrorMessage}
            autoFocus
            data-cy='login-password'
          />
          {genericErrorMessage ? <ErrorMessageWithMargin>{genericErrorMessage}</ErrorMessageWithMargin> : null}
          <AuthActionButton value={loginCTA ?? 'Log in'} disabled={requestLoading} data-cy='login-button' />
          <AuthSecondaryButton onClick={goBack} label='Log in with different account' />
          <AuthSecondaryButton onClick={handleForgotPassword} label='Forgot password' />
        </fieldset>
      </form>
    </>
  );
};
